import React, { useState } from 'react';
import CollapsibleTable from '../CollapsibleTable';
import VisualizationRenderer from '../visualizations/VisualizationRenderer';
import BrandedPieChart from './BrandedPieChart';
import CollapsibleSection from '../common/CollapsibleSection';
import AIInsightCard from './AIInsightCard';
import CPGInsightCard from './CPGInsightCard';
import InsightToggle, { InsightView } from './InsightToggle';
import KeyFindingsSummary from './KeyFindingsSummary';
import ConfidenceIndicator from './ConfidenceIndicator';
import ImageHotspotHeatmap from './ImageHotspotHeatmap';
import AnalysisMarkdown from '../common/AnalysisMarkdown';
import { MessageSquare } from 'lucide-react';
import { QuestionData, ResponseDistribution } from './types';

/**
 * Question Type to Visualization Mapping:
 * 
 * SINGLE_CHOICE     -> Horizontal Bar Chart
 * MULTIPLE_CHOICE   -> Horizontal Bar Chart
 * YES_NO            -> Branded Pie Chart (with brand colors)
 * RATING_SCALE      -> Horizontal Bar Chart
 * LIKERT_SCALE      -> Horizontal Bar Chart
 * RANKING           -> Heatmap + Stacked Bar Chart
 * SHORT_TEXT        -> AI Summary (in analysis section)
 * LONG_TEXT         -> AI Summary (in analysis section)
 * DROPDOWN          -> Horizontal Bar Chart
 * MATRIX_GRID       -> Horizontal Bar Chart
 * NPS               -> Horizontal Bar Chart
 * NUMERIC_INPUT     -> Horizontal Bar Chart
 * QUALITATIVE       -> AI Summary (in analysis section)
 * IMAGE_HOTSPOT     -> Visual Heatmap on Image + Ranking Heatmap + Bar Chart
 */

interface QuestionRendererProps {
  question: QuestionData;
  index: number;
  isExporting?: boolean;
  className?: string;
  images?: string[];
  onImageClick?: (src: string, alt: string) => void;
}

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper function to create stacked chart data from ranking breakdown - dynamic positions
const createStackedDataFromRanking = (distribution: ResponseDistribution[]): any[] => {
  return distribution.map(item => {
    if (!item.ranking_breakdown) {
      return { 
        category: item.option, 
        value: item.count.toString(),
        percentage: `${item.percentage}%`
      };
    }
    
    // BluePill AI brand color palette
    const positionColors = [
      "#07E5D1",  // Primary Teal for 1st
      "#06D4C2",  // Primary Hover for 2nd  
      "#10B981",  // Success Green for 3rd
      "#3B82F6",  // Info Blue for 4th
      "#F59E0B",  // Warning Amber for 5th
      "#EF4444",  // Error Red for 6th
      "#1C1C1E",  // Text Primary for 7th
      "#374151"   // Text Medium for 8th
    ];
    
    // Generate stacked data dynamically from all ranking positions
    const stacked_data = Object.entries(item.ranking_breakdown)
      .sort(([a], [b]) => {
        // Sort by position number (1st, 2nd, 3rd, etc.)
        const aNum = parseInt(a.match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.match(/\d+/)?.[0] || '0');
        return aNum - bNum;
      })
      .map(([key, value], index) => {
        // Convert backend key format (1st_place) to display format (1st Place)
        const positionNum = parseInt(key.match(/\d+/)?.[0] || '0');
        const suffix = positionNum === 1 ? 'st' : positionNum === 2 ? 'nd' : positionNum === 3 ? 'rd' : 'th';
        const label = `${positionNum}${suffix} Place`;
        
        const colorIndex = index % positionColors.length;
        return {
          label: label,
          value: value,
          color: positionColors[colorIndex]
        };
      });
    
    return {
      category: item.option,
      value: item.count.toString(),
      percentage: `${item.percentage}%`,
      stacked_data
    };
  });
};

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  index,
  isExporting = false,
  className = '',
  images = [],
  onImageClick
}) => {
  const cpgAnalysis = question.cpg_analysis;
  const hasCpg = !!cpgAnalysis && !!(cpgAnalysis.tldr || cpgAnalysis.synthesis);
  const hasCpgTldr = !!cpgAnalysis?.tldr;
  const [insightView, setInsightView] = useState<InsightView>(hasCpg ? 'cpg' : 'ai');
  const [findingsView, setFindingsView] = useState<InsightView>(hasCpgTldr ? 'cpg' : 'ai');

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 1. Question Header */}
      <div className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-base font-medium text-gray-900 leading-relaxed flex-1">
          Q{index + 1}: {question.question_text}
        </h3>
          {/* Confidence Indicator (when context optimization is enabled) */}
          {question.context_optimization && (
            <ConfidenceIndicator 
              data={question.context_optimization} 
              compact={true} 
            />
          )}
        </div>
        <div className="flex items-center space-x-4 mt-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {question.question_type.replace(/_/g, ' ')}
          </span>
          <span className="text-xs text-gray-500">
            {question.total_responses} responses
          </span>
        </div>
        
        {/* Question-level images - display like SimulationInputsDropdown */}
        {question.image_paths && question.image_paths.length > 0 && question.image_attachment_level !== 'option' && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {question.image_paths.map((imagePath, imgIndex) => {
                const fullImageUrl = imagePath.startsWith('http') ? imagePath : `${API_URL}${imagePath}`;
                const imageDescription = question.image_descriptions?.[imgIndex];
                
                return (
                  <div key={imgIndex} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {/* Image */}
                    <img
                      src={fullImageUrl}
                      alt={`Question Context - Image ${imgIndex + 1}`}
                      className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => onImageClick?.(fullImageUrl, `Question Context - Image ${imgIndex + 1}`)}
                    />
                    
                    {/* AI Description if available */}
                    {imageDescription && (
                      <div className="p-3 bg-gray-50">
                        <span className="text-xs font-semibold text-gray-600 uppercase flex items-center mb-1">
                          AI Description
                        </span>
                        <p className="text-xs text-gray-700 italic line-clamp-3">
                          {imageDescription}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 2. Enhanced Visualizations based on Question Type */}
      {question.analysis?.quantitative_analysis?.response_distribution ? (
        <div className="space-y-4">
          {/* HORIZONTAL BAR CHART - for single/multiple choice, dropdown, NPS, numeric input */}
          {['single_choice', 'multiple_choice', 'dropdown', 'nps', 'numeric_input'].includes(question.question_type) && (
            <VisualizationRenderer 
              visualization={{
                type: 'bar_chart',
                title: `${question.question_text} - Response Distribution`,
                data: question.analysis.quantitative_analysis.response_distribution.map(item => ({
                  category: item.option,
                  name: item.option,
                  value: item.count.toString(),
                  percentage: `${item.percentage}%`
                }))
              }}
              questionType={question.question_type}
              optionImages={question.option_images}
              imageAttachmentLevel={question.image_attachment_level}
            />
          )}
          
          {/* STACKED BAR CHART - for rating scale, likert scale, matrix grid */}
          {['rating_scale', 'likert_scale', 'matrix_grid'].includes(question.question_type) && (
            <VisualizationRenderer 
              visualization={{
                type: 'bar_chart',
                title: `${question.question_text} - Response Distribution`,
                data: question.analysis.quantitative_analysis.response_distribution.map(item => ({
                  category: item.option,
                  name: item.option,
                  value: item.count.toString(),
                  percentage: `${item.percentage}%`
                }))
              }}
              questionType={question.question_type}
              optionImages={question.option_images}
              imageAttachmentLevel={question.image_attachment_level}
            />
          )}
          
          {/* BRANDED PIE CHART - for yes/no questions */}
          {question.question_type === 'yes_no' && question.analysis?.quantitative_analysis?.response_distribution ? (
            <BrandedPieChart 
              data={question.analysis.quantitative_analysis.response_distribution.map(item => ({
                option: item.option,
                count: item.count,
                percentage: item.percentage
              }))}
              title={`${question.question_text} - Response Distribution`}
            />
          ) : question.question_type === 'yes_no' ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">
                Pie chart data not available for this yes/no question. 
                Question type: {question.question_type}
              </p>
            </div>
          ) : null}
          
          {/* RANKING QUESTIONS - Heatmap only */}
          {question.question_type === 'ranking' && (
            <>
              {/* Heatmap for Ranking Questions */}
              {question.analysis.quantitative_analysis.response_distribution[0]?.ranking_breakdown && (
                <VisualizationRenderer 
                  visualization={{
                    type: 'heatmap',
                    title: `${question.question_text} - Ranking Heatmap`,
                    data: createStackedDataFromRanking(question.analysis.quantitative_analysis.response_distribution)
                  }}
                  questionType={question.question_type}
                />
              )}
            </>
          )}
          
          {/* MAX DIFF QUESTIONS - Net score chart */}
          {question.question_type === 'max_diff' && (
            <>
              {/* Net Score Bar Chart (Most% - Least%) - delta, not percentage */}
              <VisualizationRenderer 
                visualization={{
                  type: 'bar_chart',
                  title: `${question.question_text} - Net Preference Score`,
                  data: question.analysis.quantitative_analysis.response_distribution.map(item => ({
                    category: item.option,
                    name: item.option,
                    value: item.percentage.toString(),
                    percentage: `${item.percentage}`
                  }))
                }}
                questionType={question.question_type}
              />
            </>
          )}

          {/* IMAGE HOTSPOT QUESTIONS - Visual heatmap on image + stats */}
          {question.question_type === 'image_hotspot' && (
            <>
              {/* Visual Heatmap on Image - shows all clicks overlaid on product image */}
              {question.hotspot_image_url && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">Click Heatmap</h4>
                  <ImageHotspotHeatmap
                    imageUrl={question.hotspot_image_url.startsWith('http') 
                      ? question.hotspot_image_url 
                      : `${API_URL}${question.hotspot_image_url}`}
                    clicks={question.hotspot_aggregated_clicks || []}
                    boundingBoxes={question.hotspot_bounding_boxes || []}
                    totalRespondents={question.total_responses}
                  />
                </div>
              )}
              
              {/* Ranking breakdown heatmap - shows products vs ranking positions */}
              {question.analysis.quantitative_analysis.response_distribution[0]?.ranking_breakdown && (
                <VisualizationRenderer 
                  visualization={{
                    type: 'heatmap',
                    title: `${question.question_text} - Ranking Breakdown`,
                    data: createStackedDataFromRanking(question.analysis.quantitative_analysis.response_distribution)
                  }}
                  questionType={question.question_type}
                />
              )}
              
              {/* Bar chart showing overall product selection frequency */}
              <VisualizationRenderer 
                visualization={{
                  type: 'bar_chart',
                  title: `${question.question_text} - Selection Distribution`,
                  data: question.analysis.quantitative_analysis.response_distribution.map(item => ({
                    category: item.option,
                    name: item.option,
                    value: item.count.toString(),
                    percentage: `${item.percentage}%`
                  }))
                }}
                questionType={question.question_type}
              />
            </>
          )}
          
          {/* AI SUMMARY - for text questions (handled in analysis section above) */}
          {/* Short text, long text, and qualitative questions show AI insights in the analysis section */}
        </div>
      ) : null}

      {/* Legacy Visualization (if exists and not covered above) */}
      {question.visualization && 
       !['ranking', 'multiple_choice', 'single_choice', 'rating_scale', 'likert_scale', 'matrix_grid', 'dropdown', 'nps', 'numeric_input', 'yes_no', 'qualitative', 'short_text', 'long_text', 'max_diff', 'image_hotspot'].includes(question.question_type) && (
        <VisualizationRenderer 
          visualization={question.visualization} 
          questionType={question.question_type}
        />
      )}

      {/* 3. Key Findings Summary Section - After Visualizations, Before AI Insights */}
      {(() => {
        const hasKeyFindings = question.analysis?.ai_insights &&
          typeof question.analysis.ai_insights === 'object' &&
          'key_findings' in question.analysis.ai_insights &&
          question.analysis.ai_insights.key_findings;
        const showFindingsToggle = hasKeyFindings && hasCpgTldr;

        if (!hasKeyFindings && !hasCpgTldr) return null;

        return (
          <div>
            <CollapsibleSection
              title="Key Findings Summary"
              showDot={true}
              defaultExpanded={true}
              headerRight={showFindingsToggle ? <InsightToggle activeView={findingsView} onToggle={setFindingsView} aiLabel="Key Findings" cpgLabel="TL;DR" /> : undefined}
            >
              {findingsView === 'ai' && hasKeyFindings && (
                <KeyFindingsSummary
                  finding={(question.analysis!.ai_insights as any).key_findings}
                  totalRespondents={(question.analysis!.ai_insights as any).total_respondents}
                />
              )}
              {findingsView === 'cpg' && hasCpgTldr && (
                <div className="bg-white border border-gray-200 rounded-lg p-5">
                  <div className="flex items-start space-x-3">
                    <MessageSquare className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-2">TL;DR</p>
                      <AnalysisMarkdown>{cpgAnalysis!.tldr}</AnalysisMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </CollapsibleSection>
          </div>
        );
      })()}
      
      {/* Context Optimization Details (when enabled) */}
      {question.context_optimization && (
        <div>
          <CollapsibleSection 
            title="Response Confidence Analysis" 
            showDot={true}
            defaultExpanded={false}
          >
            <ConfidenceIndicator 
              data={question.context_optimization} 
              showDetails={true}
              compact={false}
            />
          </CollapsibleSection>
        </div>
      )}

      {/* 4. AI Insights Section (with toggle when CPG analysis available) */}
      {(question.analysis?.ai_insights || hasCpg) && (
        <div>
          <CollapsibleSection
            title="AI Insights"
            showDot={true}
            defaultExpanded={true}
            headerRight={hasCpg ? <InsightToggle activeView={insightView} onToggle={setInsightView} /> : undefined}
          >
            {insightView === 'ai' && question.analysis?.ai_insights && (
              <AIInsightCard insights={question.analysis.ai_insights} />
            )}
            {insightView === 'cpg' && cpgAnalysis && (
              <CPGInsightCard analysis={cpgAnalysis} />
            )}
          </CollapsibleSection>
        </div>
      )}

      {/* 5. Response Table - Show full content when expanded */}
      <CollapsibleTable 
        table={question.table}
        questionType={question.question_type}
        isExporting={isExporting}
        showFullContent={true} // This enables showing full responses when expanded
        showExportButtons={true} // Enable export buttons (copy markdown, export Excel)
        images={images}
        optionImages={question.option_images}
        imageAttachmentLevel={question.image_attachment_level}
        onImageClick={onImageClick}
      />
    </div>
  );
};

export default QuestionRenderer;